import { MainLayout } from '@components/layouts/main.layout';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { LlmLogModalDetails } from '@components/shared/llm-log-modal-details.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { useAdminLlmLogQuery, useAdminLlmLogsQuery } from '@queries/llm.query';
import { SortingState } from '@tanstack/react-table';
import { RefreshCcwIcon } from 'lucide-react';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { parseAsSortingState } from '../../../../utils/sorting.util';
import { IconButton } from '../../../core/icon-button.component';
import { AdminLlmPagination } from './components/admin-llm-pagination.component';
import { AdminLlmTable } from './components/admin-llm-table.component';

const PAGE_SIZE = 25;

export const AdminLlmPage: React.FC = () => {
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(PAGE_SIZE));
  const [selectedLogId, setSelectedLogId] = useQueryState('logId', parseAsString);
  const [sorting, setSorting] = useQueryState(
    'sorting',
    parseAsSortingState.withDefault([{ desc: true, id: 'createdAt' }]),
  );

  const {
    data,
    isPending,
    isFetching,
    refetch: refetchLogs,
  } = useAdminLlmLogsQuery({
    page,
    limit,
  });

  const tableRef = useRef<HTMLDivElement>(null);
  const previousPageRef = useRef<number>(page);

  const logs = data?.logs ?? [];
  const pagination = data?.pagination;

  useEffect(() => {
    if (!pagination) return;

    if (pagination.totalPages === 0 && page !== 1) {
      setPage(1);
      return;
    }

    if (pagination.totalPages > 0 && page > pagination.totalPages) {
      setPage(pagination.totalPages);
    }
  }, [pagination, page, setPage]);

  const closeDetails = useCallback(() => {
    setSelectedLogId(null);
  }, [setSelectedLogId]);

  const selectedLogFromList = useMemo(
    () => (selectedLogId ? logs.find((log) => log.id === selectedLogId) : undefined),
    [logs, selectedLogId],
  );

  const { data: selectedLogDetails, isPending: isDetailsLoading } = useAdminLlmLogQuery(
    selectedLogId ?? undefined,
    selectedLogFromList,
  );

  const handlePreviousPage = useCallback(() => {
    if (pagination?.hasPreviousPage) {
      setPage((prev) => Math.max(1, (prev ?? 1) - 1));
    }
  }, [pagination?.hasPreviousPage, setPage]);

  const handleNextPage = useCallback(() => {
    if (pagination?.hasNextPage) {
      setPage((prev) => (prev ?? 1) + 1);
    }
  }, [pagination?.hasNextPage, setPage]);

  // Scroll to top when page changes and data is loaded
  useEffect(() => {
    if (!isFetching && tableRef.current && previousPageRef.current !== page) {
      document?.documentElement?.scrollIntoView({ behavior: 'smooth' });
      previousPageRef.current = page;
    }
  }, [page, isFetching]);

  const handleRowClick = useCallback(
    (logId: string) => {
      setSelectedLogId(logId);
    },
    [setSelectedLogId],
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setSorting(newSorting);
    },
    [setSorting],
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      setLimit(newLimit);
      // Reset to page 1 when changing limit to avoid edge cases
      setPage(1);
    },
    [setLimit, setPage],
  );

  return (
    <PageviewTracker>
      <HelmetWrapper title="LLM Admin" />
      <MainLayout
        asideClassName="hidden"
        contentClassName="w-full md:max-w-full"
      >
        <div className="flex flex-col w-full gap-4">
          <Tile
            header={{
              title: 'LLM Logs',
              actions: (
                <IconButton
                  icon={<RefreshCcwIcon />}
                  disabled={isFetching}
                  onClick={() => {
                    refetchLogs();
                  }}
                  variant="secondary"
                />
              ),
            }}
            isLoading={isPending}
            isEmpty={!isPending && logs.length === 0}
            emptyState={{
              message: 'As we interact with LLMs, you will see the full history here.',
              title: 'No Logs Found',
            }}
          >
            <section ref={tableRef}>
              <AdminLlmTable
                logs={logs}
                selectedLogId={selectedLogId}
                sorting={sorting}
                onRowClick={handleRowClick}
                onSortingChange={handleSortingChange}
              />
              {pagination && (
                <AdminLlmPagination
                  pagination={pagination}
                  isFetching={isFetching}
                  onPreviousPage={handlePreviousPage}
                  onNextPage={handleNextPage}
                  onLimitChange={handleLimitChange}
                />
              )}
            </section>
          </Tile>
        </div>
      </MainLayout>

      {selectedLogId && (
        <LlmLogModalDetails
          isOpen={true}
          isLoading={isDetailsLoading}
          log={selectedLogDetails ?? selectedLogFromList ?? undefined}
          onClose={closeDetails}
        />
      )}
    </PageviewTracker>
  );
};
